import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/hooks/useTheme";
import { type LightVariant, type DarkVariant } from "@/contexts/AppContext";
import { cn } from "@/lib/utils";

interface LightVariantOption {
  value: LightVariant;
  label: string;
  description: string;
  preview: string[];
}

interface DarkVariantOption {
  value: DarkVariant;
  label: string;
  description: string;
  preview: string[];
}

export function VariantPicker() {
  const { t } = useTranslation();
  const { displayTheme, lightVariant, darkVariant, setLightVariant, setDarkVariant } = useTheme();

  const lightVariantOptions: LightVariantOption[] = [
    {
      value: "default",
      label: t('variantDefault'),
      description: t('variantDefaultDescription'),
      preview: ['bg-slate-100', 'bg-slate-200', 'bg-white'],
    },
    {
      value: "soft",
      label: t('variantSoft'),
      description: t('variantSoftDescription'),
      preview: ['bg-amber-50', 'bg-orange-100', 'bg-stone-50'],
    },
    {
      value: "cool",
      label: t('variantCool'),
      description: t('variantCoolDescription'),
      preview: ['bg-sky-100', 'bg-blue-100', 'bg-slate-50'],
    },
  ];

  const darkVariantOptions: DarkVariantOption[] = [
    {
      value: "default",
      label: t('variantDefault'),
      description: t('variantDefaultDarkDescription'),
      preview: ['bg-slate-800', 'bg-slate-900', 'bg-slate-950'],
    },
    {
      value: "midnight",
      label: t('variantMidnight'),
      description: t('variantMidnightDescription'),
      preview: ['bg-blue-900', 'bg-indigo-950', 'bg-slate-950'],
    },
    {
      value: "amoled",
      label: t('variantAmoled'),
      description: t('variantAmoledDescription'),
      preview: ['bg-neutral-900', 'bg-neutral-950', 'bg-black'],
    },
    {
      value: "nord",
      label: t('variantNord'),
      description: t('variantNordDescription'),
      preview: ['bg-slate-700', 'bg-slate-800', 'bg-slate-900'],
    },
    {
      value: "dracula",
      label: t('variantDracula'),
      description: t('variantDraculaDescription'),
      preview: ['bg-purple-900', 'bg-slate-900', 'bg-slate-950'],
    },
    {
      value: "catppuccin",
      label: t('variantCatppuccin'),
      description: t('variantCatppuccinDescription'),
      preview: ['bg-indigo-900', 'bg-slate-900', 'bg-slate-950'],
    },
  ];

  const isLight = displayTheme === 'light';
  const currentVariant = isLight ? lightVariant : darkVariant;
  const options = isLight ? lightVariantOptions : darkVariantOptions;
  const currentOption = options.find(opt => opt.value === currentVariant);

  const handleSelect = (value: string) => {
    if (isLight) {
      setLightVariant(value as LightVariant);
    } else {
      setDarkVariant(value as DarkVariant);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full justify-between h-10">
          <div className="flex items-center gap-3">
            <div className="flex gap-0.5">
              {currentOption?.preview.map((color, i) => (
                <div
                  key={i}
                  className={cn("w-3 h-3 rounded-full border border-border/50", color)}
                />
              ))}
            </div>
            <span className="text-sm">{currentOption?.label || t('variantDefault')}</span>
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px]">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className="flex items-center gap-3 py-2"
          >
            <div className="flex gap-0.5">
              {option.preview.map((color, i) => (
                <div
                  key={i}
                  className={cn("w-3 h-3 rounded-full border border-border/50", color)}
                />
              ))}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium">{option.label}</span>
              <p className="text-xs text-muted-foreground truncate">{option.description}</p>
            </div>
            {currentVariant === option.value && (
              <Check className="h-4 w-4 text-primary shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
